import { Mic } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import Sidebar from './components/Sidebar';
import NoteEditor from './components/NoteEditor';
import { recordingNotesApi } from './api';
import './Recording.css';

export default function RecordingWorkspace({ userId }) {
  const api = useMemo(() => recordingNotesApi(userId), [userId]);
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => onSnapshot(api.notes, (snapshot) => {
    const next = snapshot.docs.map((item) => ({ ...item.data(), id: item.id, preview: item.data().content?.slice(0, 160) }))
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    setNotes(next);
    setActiveId((id) => next.some((note) => note.id === id) ? id : next[0]?.id || null);
    setLoading(false);
  }, (err) => { setError(err.message); setLoading(false); }), [api]);
  const activeNote = notes.find((note) => note.id === activeId);
  const save = async (id, patch) => {
    try { await api.updateNote(id, patch); setError(''); }
    catch (err) { setError(`Could not save note: ${err.message}`); }
  };
  const create = async () => {
    try { const note = await api.create(); setActiveId(note.id); setError(''); }
    catch (err) { setError(`Could not create note: ${err.message}`); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this recording note and its transcript?')) return;
    try { await api.deleteNote(id); setError(''); }
    catch (err) { setError(`Could not delete note: ${err.message}`); }
  };
  return (
    <section className="recording-workspace" aria-label="Recording">
      <div className="recording-heading">
        <Mic aria-hidden="true" />
        <div><h2>Recording</h2><p>Capture audio, transcribe conversations, and keep your notes together.</p></div>
      </div>
      {error && <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? <p>Loading recording notes…</p> : <div className="app">
        <Sidebar notes={notes} activeId={activeId} onSelect={setActiveId} onCreate={create} onDelete={remove} />
        <div className="main-panel">
          {activeNote ? <NoteEditor key={activeNote.id} note={activeNote} api={api} onSave={save} onError={setError} />
            : <div className="empty-state">Create a note to start recording and transcribing.</div>}
        </div>
      </div>}
    </section>
  );
}
